import { RestClient } from '@pegasus/rest-client';
import { getCSRFCookie } from '@browsing/utils';
import { Cart, RecoveredCart, AddItemProps, transformCart, getNow } from '@pegasus/cart';
import { CSRF_HEADER, XSRF } from '@shared/constants';
import { logError } from '@pegasus/sentry';

// Consolidated error logging for all cart operations
const captureCartError = (error: Error, operation: string, context: Record<string, any> = {}) => {
    console.error(`MYLOUNGE ${operation} - Request failed:`, error);

    // Enhance error with context for Sentry
    const enhancedError = new Error(`Cart ${operation} failed: ${error.message}`);
    enhancedError.stack = error.stack;
    (enhancedError as any).originalError = error;
    (enhancedError as any).service = 'cart';
    (enhancedError as any).operation = operation;
    (enhancedError as any).context = context;

    logError(`Cart ${operation} failed: ${error.message}`, {
        enhancedError,
    });
};

// Common headers for all cart requests
const getBaseHeaders = () => ({
    client_type: 'web',
    'X-Requested-With': 'XMLHttpRequest',
});

// Headers for requests that modify cart state (POST, PUT, DELETE)
const getCartStateChangeHeaders = () => ({
    ...getBaseHeaders(),
    [CSRF_HEADER]: getCSRFCookie(XSRF) || '',
});

// Common response handler for 204 responses
const handleCartResponse = (response: Response) => {
    if (response.status === 204) {
        return {};
    }
    return response.json();
};

// Empty cart structure used in multiple places
const createEmptyCart: Cart = {
    items: [] as Cart['items'],
    expiresIn: 0,
    expiresAt: getNow(),
    savings: 0,
    total: 0,
    shippingCosts: 0,
    discountPercent: 0,
    createdAt: getNow(),
};

export const getCart = (): Promise<Cart> => {
    const headers = { ...getBaseHeaders(), 'x-enable-unreserved-cart': 'true' };

    return RestClient.get('/api/phoenix/stockcart/cart')
        .withCredentials('include')
        .withHeaders(headers)
        .withTimeout(8000)
        .acceptStatusCodes([200, 204])
        .withSpanName('GET - /phoenix/stockcart/cart')
        .send()
        .then(async response => {
            if (response.status === 204) {
                return createEmptyCart;
            }
            const parsedResponse = await response.json();
            return transformCart(parsedResponse);
        })
        .catch((error): Cart => {
            captureCartError(error, 'getCart', {
                headers,
            });

            return createEmptyCart;
        });
};

export const extendCart = () => {
    const headers = {
        ...getCartStateChangeHeaders(),
        'Content-Type': 'application/json',
        'x-enable-unreserved-cart': 'true',
    };

    return RestClient.put('/api/phoenix/stockcart/cart')
        .withCredentials('include')
        .withHeaders(headers)
        .acceptStatusCodes([200, 204])
        .withSpanName('PUT - /phoenix/stockcart/cart')
        .send()
        .then(handleCartResponse)
        .catch(error => {
            captureCartError(error, 'extendCart', {
                headers,
            });
            throw error;
        });
};

export const deleteCartItem = (itemSku: string) => {
    const headers = {
        ...getCartStateChangeHeaders(),
        'x-enable-unreserved-cart': 'true',
    };

    return RestClient.delete(`/api/phoenix/stockcart/cart/items/${itemSku}`)
        .withCredentials('include')
        .withHeaders(headers)
        .acceptStatusCodes([200, 204])
        .withSpanName('DELETE - /phoenix/stockcart/cart/items')
        .send()
        .then(handleCartResponse)
        .catch(error => {
            captureCartError(error, 'deleteCartItem', {
                itemSku,
            });
            throw error;
        });
};

export const deleteAllItemsFromCart = () => {
    const headers = {
        ...getCartStateChangeHeaders(),
        'x-enable-unreserved-cart': 'true',
    };

    return RestClient.delete('/api/phoenix/stockcart/cart')
        .withCredentials('include')
        .withHeaders(headers)
        .acceptStatusCodes([200, 204])
        .withSpanName('DELETE - /phoenix/stockcart/cart')
        .send()
        .then(handleCartResponse)
        .catch(error => {
            captureCartError(error, 'deleteAllItemsFromCart');
            throw error;
        });
};

export const addItemToCart = ({ quantity = 1, ...props }: AddItemProps) => {
    const headers = {
        ...getCartStateChangeHeaders(),
        'Content-Type': 'application/json',
        'x-enable-unreserved-cart': 'true',
    };

    const { ...addToCartProps } = props;
    const payload = { quantity, ...addToCartProps };

    return RestClient.post('/api/phoenix/stockcart/cart/items')
        .withCredentials('include')
        .withHeaders(headers)
        .withPayload(payload)
        .acceptStatusCodes([200, 204, 409])
        .withSpanName('POST - /phoenix/stockcart/cart/items')
        .send()
        .then(async response => {
            // Handle 204 No Content responses - but this should return the proper response structure
            if (response.status === 204) {
                return {};
            }

            const data = await response.json();

            if (response.status === 409) {
                return { error: data };
            }

            return data;
        })
        .catch(error => {
            captureCartError(error, 'addItemToCart', {
                payload,
            });
            throw error;
        });
};

export const reserveCart = (): Promise<Cart> => {
    const headers = {
        ...getCartStateChangeHeaders(),
        'Content-Type': 'application/json',
    };

    return RestClient.post('/api/phoenix/stockcart/cart/reserve')
        .withCredentials('include')
        .withHeaders(headers)
        .acceptStatusCodes([200])
        .withSpanName('POST - /phoenix/stockcart/cart/reserve')
        .send()
        .then(async response => {
            const parsedResponse = await response.json();
            return transformCart(parsedResponse);
        })
        .catch(error => {
            captureCartError(error, 'reserveCart', { headers });
            throw error;
        });
};

export const getRecoveredCart = (): Promise<RecoveredCart> =>
    RestClient.get('/api/recovered-cart')
        .withCredentials('include')
        .withHeaders(getBaseHeaders())
        .acceptStatusCodes([200, 204])
        .withSpanName('GET - /api/recovered-cart')
        .send()
        .then(response => {
            // Handle 204 No Content responses for RecoveredCart type
            if (response.status === 204) {
                return { items: [] } as RecoveredCart;
            }
            return response.json();
        })
        .catch(error => {
            captureCartError(error, 'getRecoveredCart', {});
            throw error;
        });
